import { useUpgradeStore } from "../../stores/upgradeStore.js";
import { useWorkflowStore } from "../../stores/workflowStore.js";
import { Weapon } from "../classes/Weapon.js";
import { Materials } from "../data/materials.js";
import { WeaponSubtypes } from "../data/weaponSubtypes.js";
import { emit, GameEvents, on } from "./eventBus.js";

function getCraftCount() {
  const upgrades = useUpgradeStore();
  if (upgrades.hasPurchased("smithy_craft_3")) return 3;
  if (upgrades.hasPurchased("smithy_craft_2")) return 2;
  return 1;
}

function getCraftTime() {
  const upgrades = useUpgradeStore();
  const base = 180;
  if (upgrades.hasPurchased("smithy_craft_speed")) return Math.floor(base * 0.5);
  return base;
}

function getSmeltCount() {
  const upgrades = useUpgradeStore();
  if (upgrades.hasPurchased("smithy_smelt_3")) return 3;
  if (upgrades.hasPurchased("smithy_smelt_2")) return 2;
  return 1;
}

function getSmeltTime() {
  const upgrades = useUpgradeStore();
  const base = 180;
  if (upgrades.hasPurchased("smithy_smelt_speed")) return Math.floor(base * 0.5);
  return base;
}

export function initCraftingEvents(gameStore, inventoryStore) {
  on(GameEvents.ITEM_CREATED, ({ item }) => {
    emit(GameEvents.ITEM_ADDED, { item });
    useWorkflowStore().processWorkflows("onSmithed", item);
  });

  on(GameEvents.SMELT_QUEUED, ({ item }) => {
    const workflow = useWorkflowStore();
    const index = inventoryStore.items.indexOf(item);
    if (index === -1) return;

    const queueItem = { ...item, quantity: 1 };
    if (item.quantity > 1) {
      item.quantity--;
    } else {
      inventoryStore.items.splice(index, 1);
    }
    workflow.smeltingQueue.push(queueItem);
  });

  on(GameEvents.SMELT_CANCELLED, ({ item }) => {
    const workflow = useWorkflowStore();
    const index = workflow.smeltingQueue.indexOf(item);
    if (index === -1) return;
    workflow.smeltingQueue.splice(index, 1);
    inventoryStore.addItem(item);
  });

  on(GameEvents.SMITH_QUEUED, ({ recipe }) => {
    const workflow = useWorkflowStore();
    inventoryStore.materials[recipe.material] -= 3;
    workflow.smithingQueue.push(recipe);
  });

  on(GameEvents.SMITH_TICK, () => {
    const workflow = useWorkflowStore();
    if (workflow.smithingQueue.length === 0) {
      workflow.isSmithing = false;
      workflow.smithingProgress = 0;
      return;
    }

    workflow.isSmithing = true;
    workflow.smithingProgress++;

    const craftTime = getCraftTime();
    if (workflow.smithingProgress >= craftTime) {
      const craftCount = getCraftCount();
      for (let i = 0; i < craftCount && workflow.smithingQueue.length > 0; i++) {
        emit(GameEvents.SMITH_COMPLETE, { recipe: workflow.smithingQueue[0] });
      }
    }
  });

  on(GameEvents.SMITH_COMPLETE, ({ recipe }) => {
    const workflow = useWorkflowStore();
    workflow.smithingQueue.shift();
    const material = Object.values(Materials).find(
      (m) => m.name === recipe.material
    );
    const subtype = Object.values(WeaponSubtypes).find(
      (s) => s.name === recipe.subtype
    );
    const weapon = Weapon.createRandom(subtype, material);
    emit(GameEvents.ITEM_CREATED, { item: weapon });
    workflow.smithingProgress = 0;
  });

  on(GameEvents.SMELT_TICK, () => {
    const workflow = useWorkflowStore();
    if (workflow.smeltingQueue.length === 0) {
      workflow.isSmelting = false;
      workflow.smeltingProgress = 0;
      return;
    }

    workflow.isSmelting = true;
    workflow.smeltingProgress++;

    const smeltTime = getSmeltTime();
    if (workflow.smeltingProgress >= smeltTime) {
      const smeltCount = getSmeltCount();
      for (let i = 0; i < smeltCount && workflow.smeltingQueue.length > 0; i++) {
        emit(GameEvents.SMELT_COMPLETE, { item: workflow.smeltingQueue[0] });
      }
    }
  });

  on(GameEvents.SMELT_COMPLETE, ({ item }) => {
    const workflow = useWorkflowStore();
    workflow.smeltingQueue.shift();
    inventoryStore.addMaterial(item.material.name);
    workflow.smeltingProgress = 0;
  });
}
